import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, Trash2, Save, X } from "lucide-react";
import {
  costRecipeSchema,
  deriveCostRecipe,
  materialExtendedPrice,
  type CostRecipe,
} from "@workspace/api-zod/cost-recipe";
import type { CostRecipe as ApiCostRecipe } from "@workspace/api-client-react";
import {
  DimensionPricingConfig,
  dimensionDraftFrom,
  dimensionDraftToInput,
  type DimensionPricingDraft,
} from "@/components/material-dimension-pricing";

export type RecipeShape = "formula" | "specification";

export interface MaterialDraft {
  material_id: string;
  name: string;
  spec: string;
  base_factor: string;
  waste_pct: string;
  unit: string;
  unit_price: string;
  assumption: string;
  pack_size?: number;
  round_packs?: boolean;
  dimension_pricing?: DimensionPricingDraft;
  quantity_pricing?: DimensionPricingDraft & { reference_factor?: number };
}

export interface RecipeDraft {
  shape: RecipeShape;
  labour_allowance: string;
  plant_allowance: string;
  labour_pricing?: DimensionPricingDraft;
  plant_pricing?: DimensionPricingDraft;
  specification: string;
  supply_allowance: string;
  materials: MaterialDraft[];
  supply_name?: string;
  supply_material_id?: string;
  supply_pricing?: DimensionPricingDraft;
  /** Preserved untouched from the source recipe. */
  quote_pricing?: CostRecipe["quote_pricing"];
}

export function newMaterialId(existing: string[]): string {
  let id = "";
  do {
    id = `mat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  } while (existing.includes(id));
  return id;
}

export function emptyDraft(shape: RecipeShape): RecipeDraft {
  return { shape, labour_allowance: "", plant_allowance: "0", specification: "", supply_allowance: "", materials: [] };
}

export function draftFromRecipe(input: ApiCostRecipe | CostRecipe): RecipeDraft {
  const recipe = input as CostRecipe;
  return {
    ...(recipe.quote_pricing ? { quote_pricing: recipe.quote_pricing } : {}),
    ...(recipe.labour_pricing ? { labour_pricing: dimensionDraftFrom(recipe.labour_pricing) } : {}),
    ...(recipe.plant_pricing ? { plant_pricing: dimensionDraftFrom(recipe.plant_pricing) } : {}),
    ...(recipe.shape === "specification" && recipe.supply_name ? { supply_name: recipe.supply_name } : {}),
    ...(recipe.shape === "specification" && recipe.supply_material_id ? { supply_material_id: recipe.supply_material_id } : {}),
    ...(recipe.shape === "specification" && recipe.supply_pricing ? { supply_pricing: dimensionDraftFrom(recipe.supply_pricing) } : {}),
    shape: recipe.shape,
    labour_allowance: String(recipe.labour_allowance),
    plant_allowance: String(recipe.plant_allowance ?? 0),
    specification: recipe.shape === "specification" ? (recipe.specification ?? "") : "",
    supply_allowance: recipe.shape === "specification" ? String(recipe.supply_allowance ?? "") : "",
    materials: recipe.materials.map(m => ({
      material_id: m.material_id,
      name: m.name,
      spec: m.spec ?? "",
      base_factor: String(m.base_factor),
      waste_pct: String(m.waste_pct),
      unit: m.unit,
      unit_price: String(m.unit_price),
      assumption: m.assumption,
      ...(m.pack_size != null ? { pack_size: m.pack_size } : {}),
      ...(m.round_packs != null ? { round_packs: m.round_packs } : {}),
      ...(m.dimension_pricing ? { dimension_pricing: dimensionDraftFrom(m.dimension_pricing) } : {}),
      ...(m.quantity_pricing ? { quantity_pricing: {
        ...dimensionDraftFrom({ ...m.quantity_pricing, fixed_cost: m.quantity_pricing.fixed_quantity })!,
        reference_factor: m.quantity_pricing.reference_factor,
      } } : {}),
    })),
  };
}

function num(v: string): number {
  const t = v.trim();
  if (t === "") return NaN;
  return Number(t);
}

/** Converts the editable draft into the strict shared recipe input (numbers, not strings). */
export function draftToInput(draft: RecipeDraft, status: CostRecipe["status"]): unknown {
  const common = {
    version: 1,
    labour_allowance: num(draft.labour_allowance),
    plant_allowance: draft.plant_allowance.trim() === "" ? 0 : num(draft.plant_allowance),
    ...(draft.labour_pricing ? { labour_pricing: dimensionDraftToInput(draft.labour_pricing) } : {}),
    ...(draft.plant_pricing ? { plant_pricing: dimensionDraftToInput(draft.plant_pricing) } : {}),
    status,
    materials: draft.materials.map(m => {
      const out: Record<string, unknown> = {
        material_id: m.material_id,
        name: m.name,
        base_factor: num(m.base_factor),
        waste_pct: m.waste_pct.trim() === "" ? 0 : num(m.waste_pct),
        unit: m.unit,
        unit_price: num(m.unit_price),
        assumption: m.assumption,
      };
      if (m.spec.trim()) out.spec = m.spec;
      if (m.pack_size != null) out.pack_size = m.pack_size;
      if (m.round_packs != null) out.round_packs = m.round_packs;
      if (m.dimension_pricing) out.dimension_pricing = dimensionDraftToInput(m.dimension_pricing);
      if (m.quantity_pricing) {
        const model = dimensionDraftToInput(m.quantity_pricing);
        out.quantity_pricing = {
          measure: model.measure, reference_quantity: model.reference_quantity,
          dimension_labels: model.dimension_labels, evidence: model.evidence,
          ...(model.quantity_basis ? { quantity_basis: model.quantity_basis } : {}),
          ...(model.reference_dimensions ? { reference_dimensions: model.reference_dimensions } : {}),
          fixed_quantity: model.fixed_cost,
          ...(m.quantity_pricing.reference_factor != null ? { reference_factor: m.quantity_pricing.reference_factor } : {}),
          ...(model.resolved_quantity != null ? { resolved_quantity: model.resolved_quantity } : {}),
        };
      }
      return out;
    }),
    ...(draft.quote_pricing ? { quote_pricing: draft.quote_pricing } : {}),
  };
  if (draft.shape === "specification") {
    return { ...common, shape: "specification", specification: draft.specification, supply_allowance: num(draft.supply_allowance),
      ...(draft.supply_material_id ? { supply_material_id: draft.supply_material_id } : {}),
      ...(draft.supply_name?.trim() ? { supply_name: draft.supply_name } : {}),
      ...(draft.supply_pricing ? { supply_pricing: dimensionDraftToInput(draft.supply_pricing) } : {}) };
  }
  return { ...common, shape: "formula" };
}

export type DraftEvaluation =
  | { ok: true; recipe: CostRecipe; rateTypical: string }
  | { ok: false; errors: Record<string, string> };

const FIELD_LABELS: Record<string, string> = {
  labour_allowance: "Labour allowance",
  plant_allowance: "Plant allowance",
  specification: "Specification",
  supply_allowance: "Supply allowance",
  name: "Name",
  unit: "Unit",
  base_factor: "Base quantity",
  waste_pct: "Waste %",
  unit_price: "Unit price",
  assumption: "Assumption",
  spec: "Spec",
  reference_quantity: "Reference size",
  fixed_cost: "Fixed portion",
  evidence: "Evidence",
};

export function evaluateDraft(draft: RecipeDraft, status: CostRecipe["status"]): DraftEvaluation {
  const input = draftToInput(draft, status);
  const parsed = costRecipeSchema.safeParse(input);
  if (!parsed.success) {
    const errors: Record<string, string> = {};
    for (const issue of parsed.error.issues) {
      const key = issue.path.join(".");
      if (errors[key]) continue;
      const last = String(issue.path[issue.path.length - 1] ?? "");
      const label = FIELD_LABELS[last] ?? "Value";
      let msg = issue.message;
      if (/expected number|NaN|received nan/i.test(msg)) msg = "Enter a number";
      else if (/>=\s*0|too small/i.test(msg) && /number/i.test(msg)) msg = "Cannot be negative";
      else if (/expected string to have >=1|too small/i.test(msg)) msg = `${label} is required`;
      errors[key] = msg;
    }
    return { ok: false, errors };
  }
  try {
    const { recipe, rateTypical } = deriveCostRecipe(parsed.data);
    return { ok: true, recipe, rateTypical };
  } catch (e) {
    return { ok: false, errors: { _root: e instanceof Error ? e.message : "Recipe is invalid" } };
  }
}

/** Live extended price for one draft material row, or null if not yet numeric/valid. */
export function draftMaterialLine(m: MaterialDraft): { qty: string; extended: string } | null {
  const base = num(m.base_factor);
  const waste = m.waste_pct.trim() === "" ? 0 : num(m.waste_pct);
  const price = num(m.unit_price);
  if (![base, waste, price].every(n => Number.isFinite(n) && n >= 0)) return null;
  try {
    const q = Math.round(base * (1 + waste / 100) * 10000) / 10000;
    return { qty: q.toFixed(4).replace(/\.?0+$/, "") || "0", extended: materialExtendedPrice(q, price) };
  } catch {
    return null;
  }
}

function FieldError({ msg, id }: { msg?: string; id: string }) {
  if (!msg) return null;
  return <p className="text-xs text-destructive mt-1" data-testid={`error-${id}`}>{msg}</p>;
}

interface Props {
  taskId: string;
  unitLabel: string;
  initial: ApiCostRecipe | null;
  currentTypical: string;
  formatCurrency: (v: string | number) => string;
  isSaving: boolean;
  serverError?: string | null;
  isFirstAdd: boolean;
  onCancel: () => void;
  onSave: (recipe: CostRecipe) => void;
  /** Optional: label for the primary save button (default "Save breakdown"). */
  saveLabel?: string;
  /** Optional: show live green/red signed difference against currentTypical. */
  showBalance?: boolean;
  /** Optional: block the primary save unless derived typical equals currentTypical exactly. */
  requireExactBalance?: boolean;
  /** Optional: status written into the evaluated recipe (default "user_edited"). */
  recipeStatus?: CostRecipe["status"];
  /** Optional: secondary save action (e.g. staging save) that does not require balance. */
  secondaryAction?: { label: string; onClick: (recipe: CostRecipe) => void; testId?: string };
  /** Optional: disable all save actions (e.g. while another action runs). */
  disabled?: boolean;
}

/** Signed difference derived - current, in pence. */
export function signedPenceDifference(derived: string, current: string): number {
  return Math.round(Number(derived) * 100) - Math.round(Number(current) * 100);
}

export function CostRecipeEditor({
  taskId, unitLabel, initial, currentTypical, formatCurrency, isSaving, serverError, isFirstAdd, onCancel, onSave,
  saveLabel, showBalance, requireExactBalance, recipeStatus = "user_edited", secondaryAction, disabled,
}: Props) {
  const [draft, setDraft] = useState<RecipeDraft | null>(initial ? draftFromRecipe(initial) : null);
  const [attempted, setAttempted] = useState(false);

  const evaluation = useMemo(() => (draft ? evaluateDraft(draft, recipeStatus) : null), [draft, recipeStatus]);

  if (!draft) {
    return (
      <div className="space-y-3" data-testid={`editor-recipe-${taskId}`}>
        <p className="text-sm font-medium">What kind of item is this?</p>
        <div className="grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setDraft(emptyDraft("formula"))}
            className="text-left rounded-md border p-3 hover:bg-muted/60 transition-colors"
            data-testid={`button-shape-formula-${taskId}`}
          >
            <div className="font-medium text-sm">Formula</div>
            <div className="text-xs text-muted-foreground">Materials with a quantity per {unitLabel}, waste and unit price. Brickwork, plasterboard, tiling.</div>
          </button>
          <button
            type="button"
            onClick={() => setDraft(emptyDraft("specification"))}
            className="text-left rounded-md border p-3 hover:bg-muted/60 transition-colors"
            data-testid={`button-shape-specification-${taskId}`}
          >
            <div className="font-medium text-sm">Specification</div>
            <div className="text-xs text-muted-foreground">An assumed spec plus a supply allowance. Windows, doors, boilers.</div>
          </button>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} data-testid={`button-cancel-recipe-${taskId}`}>
          <X className="h-4 w-4 mr-1" /> Cancel
        </Button>
      </div>
    );
  }

  const d = draft;
  const errors = attempted && evaluation && !evaluation.ok ? evaluation.errors : {};
  const set = (patch: Partial<RecipeDraft>) => setDraft({ ...d, ...patch });
  const setMat = (i: number, patch: Partial<MaterialDraft>) =>
    setDraft({ ...d, materials: d.materials.map((m, j) => (j === i ? { ...m, ...patch } : m)) });
  const addMat = () =>
    setDraft({
      ...d,
      materials: [...d.materials, {
        material_id: newMaterialId(d.materials.map(m => m.material_id)),
        name: "", spec: "", base_factor: "", waste_pct: "0", unit: "", unit_price: "", assumption: "",
      }],
    });
  const removeMat = (i: number) => setDraft({ ...d, materials: d.materials.filter((_, j) => j !== i) });

  const liveTypical = evaluation?.ok ? evaluation.rateTypical : null;
  const differs = isFirstAdd && liveTypical !== null && Number(liveTypical) !== Number(currentTypical);

  const diffPence = liveTypical !== null ? signedPenceDifference(liveTypical, currentTypical) : null;
  const balanced = diffPence === 0;
  const blockedByBalance = !!requireExactBalance && !balanced;

  function submit() {
    setAttempted(true);
    if (evaluation?.ok && !blockedByBalance) onSave(evaluation.recipe);
  }
  function submitSecondary() {
    setAttempted(true);
    if (evaluation?.ok && secondaryAction) secondaryAction.onClick(evaluation.recipe);
  }

  return (
    <div className="space-y-4 min-w-0" data-testid={`editor-recipe-${taskId}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {d.shape === "formula" ? "Formula" : "Specification"} breakdown, per {unitLabel}
        </div>
        {!initial && (
          <Button type="button" variant="ghost" size="sm" className="h-auto px-2 py-1 text-xs" onClick={() => setDraft(null)} data-testid={`button-change-shape-${taskId}`}>
            Change type
          </Button>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor={`labour-${taskId}`} className="text-xs">Labour allowance (GBP per {unitLabel})</Label>
          <Input id={`labour-${taskId}`} inputMode="decimal" value={d.labour_allowance}
            onChange={e => set({ labour_allowance: e.target.value })} data-testid={`input-labour-${taskId}`} />
          <FieldError id={`labour-${taskId}`} msg={errors["labour_allowance"]} />
          <DimensionPricingConfig id={`labour-${taskId}`} itemLabel="labour" value={d.labour_pricing}
            onChange={v => set({ labour_pricing: v })} errors={errors} errorPrefix="labour_pricing" />
        </div>
        <div>
          <Label htmlFor={`plant-${taskId}`} className="text-xs">Plant allowance (GBP per {unitLabel})</Label>
          <Input id={`plant-${taskId}`} inputMode="decimal" value={d.plant_allowance}
            onChange={e => set({ plant_allowance: e.target.value })} data-testid={`input-plant-${taskId}`} />
          <FieldError id={`plant-${taskId}`} msg={errors["plant_allowance"]} />
          <DimensionPricingConfig id={`plant-${taskId}`} itemLabel="plant" value={d.plant_pricing}
            onChange={v => set({ plant_pricing: v })} errors={errors} errorPrefix="plant_pricing" />
        </div>
        {d.shape === "specification" && (
          <>
            <div className="sm:col-span-2">
              <Label htmlFor={`spec-${taskId}`} className="text-xs">Assumed specification</Label>
              <Textarea id={`spec-${taskId}`} rows={2} value={d.specification}
                onChange={e => set({ specification: e.target.value })} data-testid={`input-specification-${taskId}`}
                placeholder="uPVC casement 1200x1200, double glazed, white" />
              <FieldError id={`specification-${taskId}`} msg={errors["specification"]} />
            </div>
            <div>
              <Label htmlFor={`supply-${taskId}`} className="text-xs">Supply allowance (GBP per {unitLabel})</Label>
              <Input id={`supply-${taskId}`} inputMode="decimal" value={d.supply_allowance}
                onChange={e => set({ supply_allowance: e.target.value })} data-testid={`input-supply-${taskId}`} />
              <FieldError id={`supply-${taskId}`} msg={errors["supply_allowance"]} />
            </div>
            <div>
              <Label htmlFor={`supplyname-${taskId}`} className="text-xs">Supply item name (optional)</Label>
              <Input id={`supplyname-${taskId}`} value={d.supply_name ?? ""} placeholder="Window unit"
                onChange={e => set({ supply_name: e.target.value })} data-testid={`input-supply-name-${taskId}`} />
              <FieldError id={`supply-name-${taskId}`} msg={errors["supply_name"]} />
            </div>
            <div className="sm:col-span-2">
              <DimensionPricingConfig id={`supply-${taskId}`} itemLabel="supply" value={d.supply_pricing}
                onChange={v => set({ supply_pricing: v })} errors={errors} errorPrefix="supply_pricing" />
              <FieldError id={`supply-pricing-${taskId}`} msg={errors["supply_pricing"]} />
            </div>
          </>
        )}
      </div>

      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Materials</div>
        {d.materials.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid={`text-no-materials-${taskId}`}>No materials yet.</p>
        )}
        {d.materials.map((m, i) => {
          const line = draftMaterialLine(m);
          const p = `materials.${i}`;
          return (
            <div key={m.material_id} className="rounded-md border bg-background p-3 space-y-2" data-testid={`row-material-edit-${m.material_id}`}>
              <div className="grid gap-2 sm:grid-cols-2">
                <div>
                  <Label className="text-xs" htmlFor={`mname-${m.material_id}`}>Name</Label>
                  <Input id={`mname-${m.material_id}`} value={m.name} onChange={e => setMat(i, { name: e.target.value })} data-testid={`input-material-name-${m.material_id}`} />
                  <FieldError id={`material-name-${m.material_id}`} msg={errors[`${p}.name`]} />
                </div>
                <div>
                  <Label className="text-xs" htmlFor={`mspec-${m.material_id}`}>Spec (optional)</Label>
                  <Input id={`mspec-${m.material_id}`} value={m.spec} onChange={e => setMat(i, { spec: e.target.value })} data-testid={`input-material-spec-${m.material_id}`} />
                  <FieldError id={`material-spec-${m.material_id}`} msg={errors[`${p}.spec`]} />
                </div>
              </div>
              <div className="grid gap-2 grid-cols-2 sm:grid-cols-4">
                <div>
                  <Label className="text-xs" htmlFor={`mbase-${m.material_id}`}>Qty per {unitLabel}</Label>
                  <Input id={`mbase-${m.material_id}`} inputMode="decimal" value={m.base_factor} onChange={e => setMat(i, { base_factor: e.target.value })} data-testid={`input-material-base-${m.material_id}`} />
                  <FieldError id={`material-base-${m.material_id}`} msg={errors[`${p}.base_factor`]} />
                </div>
                <div>
                  <Label className="text-xs" htmlFor={`mwaste-${m.material_id}`}>Waste %</Label>
                  <Input id={`mwaste-${m.material_id}`} inputMode="decimal" value={m.waste_pct} onChange={e => setMat(i, { waste_pct: e.target.value })} data-testid={`input-material-waste-${m.material_id}`} />
                  <FieldError id={`material-waste-${m.material_id}`} msg={errors[`${p}.waste_pct`]} />
                </div>
                <div>
                  <Label className="text-xs" htmlFor={`munit-${m.material_id}`}>Unit</Label>
                  <Input id={`munit-${m.material_id}`} value={m.unit} placeholder="each, m², bag" onChange={e => setMat(i, { unit: e.target.value })} data-testid={`input-material-unit-${m.material_id}`} />
                  <FieldError id={`material-unit-${m.material_id}`} msg={errors[`${p}.unit`]} />
                </div>
                <div>
                  <Label className="text-xs" htmlFor={`mprice-${m.material_id}`}>Unit price (GBP)</Label>
                  <Input id={`mprice-${m.material_id}`} inputMode="decimal" value={m.unit_price} onChange={e => setMat(i, { unit_price: e.target.value })} data-testid={`input-material-price-${m.material_id}`} />
                  <FieldError id={`material-price-${m.material_id}`} msg={errors[`${p}.unit_price`]} />
                </div>
              </div>
              <div>
                <Label className="text-xs" htmlFor={`massume-${m.material_id}`}>Assumption</Label>
                <Input id={`massume-${m.material_id}`} value={m.assumption} placeholder="Half-brick wall, stretcher bond, 10mm joints" onChange={e => setMat(i, { assumption: e.target.value })} data-testid={`input-material-assumption-${m.material_id}`} />
                <FieldError id={`material-assumption-${m.material_id}`} msg={errors[`${p}.assumption`]} />
              </div>
              <DimensionPricingConfig id={m.material_id} itemLabel="this material" value={m.dimension_pricing}
                onChange={v => setMat(i, { dimension_pricing: v })} errors={errors} errorPrefix={`${p}.dimension_pricing`} />
              <DimensionPricingConfig id={`quantity-${m.material_id}`} itemLabel="this material" quantityUnit={m.unit}
                value={m.quantity_pricing} onChange={v => setMat(i, { quantity_pricing: v })}
                errors={errors} errorPrefix={`${p}.quantity_pricing`} />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground tabular-nums break-words" data-testid={`text-material-line-${m.material_id}`}>
                  {line ? `${line.qty} ${m.unit || "units"} per ${unitLabel} = ${formatCurrency(line.extended)}` : "Enter quantity and price to see the line total"}
                </span>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeMat(i)} data-testid={`button-remove-material-${m.material_id}`}>
                  <Trash2 className="h-4 w-4 mr-1" /> Remove
                </Button>
              </div>
            </div>
          );
        })}
        <FieldError id={`materials-${taskId}`} msg={errors["materials"]} />
        <Button type="button" variant="outline" size="sm" onClick={addMat} disabled={d.materials.length >= 100} data-testid={`button-add-material-${taskId}`}>
          <Plus className="h-4 w-4 mr-1" /> Add material
        </Button>
      </div>

      <div className="rounded-md bg-muted/60 p-3 flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm">Derived typical per {unitLabel}</span>
        <span className="text-lg font-semibold tabular-nums" data-testid={`text-live-typical-${taskId}`}>
          {liveTypical !== null ? formatCurrency(liveTypical) : "Incomplete"}
        </span>
        {isFirstAdd && (
          <p className={`w-full text-xs ${differs ? "text-destructive" : "text-muted-foreground"}`} data-testid={`text-first-add-hint-${taskId}`}>
            A first breakdown must add up to the current typical of {formatCurrency(currentTypical)} exactly, so the price does not change. Use labour as the balancing figure.
          </p>
        )}
        {showBalance && (
          <p
            className={`w-full text-xs font-medium tabular-nums ${diffPence === null ? "text-muted-foreground" : balanced ? "text-emerald-700 dark:text-emerald-400" : "text-destructive"}`}
            data-testid={`text-balance-${taskId}`}
          >
            {diffPence === null
              ? `Current typical ${formatCurrency(currentTypical)}. Complete the breakdown to check the balance.`
              : balanced
                ? `Matches current typical of ${formatCurrency(currentTypical)} to the penny.`
                : `Differs from current typical of ${formatCurrency(currentTypical)} by ${diffPence > 0 ? "+" : "-"}${formatCurrency(Math.abs(diffPence) / 100)}.${requireExactBalance ? " Approval needs an exact match." : ""}`}
          </p>
        )}
      </div>

      {errors["_root"] && <p className="text-sm text-destructive" data-testid={`error-root-${taskId}`}>{errors["_root"]}</p>}
      {serverError && <p className="text-sm text-destructive" data-testid={`error-save-${taskId}`}>{serverError}</p>}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isSaving} data-testid={`button-cancel-recipe-${taskId}`}>
          Cancel
        </Button>
        {secondaryAction && (
          <Button type="button" variant="outline" onClick={submitSecondary} disabled={isSaving || disabled} data-testid={secondaryAction.testId ?? `button-secondary-recipe-${taskId}`}>
            {secondaryAction.label}
          </Button>
        )}
        <Button type="button" onClick={submit} disabled={isSaving || disabled || (attempted && blockedByBalance)} data-testid={`button-save-recipe-${taskId}`}>
          {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          {isSaving ? "Saving..." : (saveLabel ?? "Save breakdown")}
        </Button>
      </div>
    </div>
  );
}
